/**
 * App Component
 * 
 * Phase 2-B: Session Cookie Authentication
 * - Authentication is determined by GET /auth/me (session cookie validation)
 * - localStorage is NO LONGER the source of truth for auth
 * - sessionChecked state prevents UI flash during auth verification
 */

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { privateRoutes, publicRoutes, webRoutes } from "./Routes/router";
import MainLayout from "./Layouts/MainLayout";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsAuthenticated,
  setLoginResponse,
} from "./store/reducers/userReducer";
import { useEffect, useState } from "react";
import { setLoginResponseCheck } from "./utils/checkStorage";
import LoginService from "./services/auth/Login";

function App() {
  const dispatch = useDispatch();
  const isLogin = useSelector((state) => state.user?.isAuthenticated);
  
  // Session check state - prevents UI flash during auth verification
  const [sessionChecked, setSessionChecked] = useState(false);
  
  // Derived auth state - true only after session is verified
  const isAuthenticated = isLogin && sessionChecked;

  useEffect(() => {
    /**
     * Session Bootstrap Flow (Phase 2-B):
     * 
     * 1. On app load/refresh, call GET /auth/me
     * 2. If successful (200): 
     *    - Session is valid
     *    - Store user data in localStorage (for UI convenience)
     *    - Dispatch to Redux
     *    - Set isAuthenticated = true
     * 3. If 401 or error:
     *    - Session is invalid/expired
     *    - Clear localStorage
     *    - Set isAuthenticated = false
     *    - User sees login page
     */
    const checkSession = async () => {
      try {
        const response = await LoginService.getMe();
        
        if (response?.status && response?.data) {
          // Session is valid - store user data and set authenticated
          const userData = response.data;
          
          // Store in localStorage for UI convenience
          localStorage.setItem("user", JSON.stringify(userData.user));
          localStorage.setItem("defaultCurrency", JSON.stringify(userData.defaultCurrency));
          localStorage.setItem("defaultLanguage", JSON.stringify(userData.defaultLanguage));
          
          // Build login response structure for Redux
          const loginResponseData = {
            user: JSON.stringify(userData.user),
            defaultCurrency: JSON.stringify(userData.defaultCurrency),
            defaultLanguage: JSON.stringify(userData.defaultLanguage),
          };
          
          dispatch(setLoginResponse(loginResponseData));
          dispatch(setIsAuthenticated(true));
        } else {
          // Response not successful - clear state
          localStorage.clear();
          dispatch(setLoginResponse(null));
          dispatch(setIsAuthenticated(false));
        }
      } catch (error) {
        // Session invalid or expired (401) - clear state and show login
        // Note: 401 errors are handled by axios interceptor which clears localStorage
        // This catch handles network errors or unexpected failures
        console.log("Session check failed:", error?.message || "Unknown error");
        localStorage.clear();
        dispatch(setLoginResponse(null));
        dispatch(setIsAuthenticated(false));
      } finally {
        // Always mark session as checked to remove loading state
        setSessionChecked(true);
      }
    };

    checkSession();
  }, [dispatch]);

  const renderPrivateRoutes = () => {
    return privateRoutes.map((route, index) => (
      <Route key={index} path={route.path} element={route.element} />
    ));
  };

  const renderWebRoutes = () => {
    return webRoutes.map((route, index) => (
      <Route key={index} path={route.path} element={route.element} />
    ));
  };

  const renderPublicRoutes = () => {
    return publicRoutes.map((route, index) => (
      <Route key={index} path={route.path} element={route.element} />
    ));
  };

  // Show loading state while checking session to prevent UI flash
  if (!sessionChecked) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: '1rem', color: '#666' }}>Verifying session...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter >
    {/* <BrowserRouter> */}
      <Routes>
        {isAuthenticated ? (
          <>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/login"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route path="/" element={<MainLayout />}>
              {renderPrivateRoutes()}
            </Route>
            {renderWebRoutes()}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        ) : (
          <>
            {renderPublicRoutes()}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

export default App;
